// MAJLIS-ADMIN (D-56, P-37, UI-C37; PROPOSED): the owner's read-only admin view, reached only by the unlinked #/admin.
// A password (held in this component's memory only - never storage, never the URL), then every room, newest first;
// a tap opens that room's full record in the SAME record screen the host sees, read-only (access "admin"). Every
// refusal is the server's one 404, shown as "Not available" and nothing more.
// MAJLIS-167 (item 4; UI-C37, UI-C14): every action is a classed button of an existing kind, and "Delete for good" is
// confirmed the MAJLIS-166 way: a ConfirmSheet (the question, ONE red --leave row, Cancel), never the inline delete bar
// of before, which is retired (test/retiredConfirm.setup.ts watches every test for it).
// MAJLIS-176 (UI-C37; the owner-approved mock-up docs/spec/prototype/admin-mockup.html, "Approve. You can start work."
// 2026-10-06; docs/design/component-rules.md): the screens redrawn, web only - the server, its three admin routes and the
// password rule are untouched.
//   (a)/(f) sign-in: ONE centred card - the lock disc, Admin, one line, the password field full width with its show/hide
//           eye, Open the one filled button; a refusal turns the field's border --leave with "Not available" under it.
//   (b)     all rooms: a header bar (Admin, the host name and when the list was loaded, Refresh as a 44 px disc, Lock a
//           muted text link that drops the password), three count tiles, a search, the state filter chips, one card per
//           room (the whole card is the tap; a deleted room is dimmed and opens nothing), "Newest first · N of N".
//   (c)     one room: the admin bar (back, name and "state · host · day-time", Delete the one red text link at the END)
//           and the facts strip (with the reading language, ruling 1) over the host's record screen drawn WITHOUT its
//           own header (the consultant's rulings 1 and 2, 2026-10-06).
//   (d)     Delete: the ConfirmSheet naming the room; a 409 (still open) is shown under the red row in --warn.
//   (e)     empty: a muted disc, "No rooms yet" and one line.
//   (h)     >= ADMIN_TABLE_FROM_PX: the same list as a table - one component, two layouts, CSS only (app.css).
// Every NEW word is held back by i18n/pending.ts until the consultant's verdict: `tp` draws its stand-in (an existing
// key) or nothing, and a part whose words are all pending is not drawn at all (whole or hold).
import { useCallback, useEffect, useState, type ReactNode } from "react";
import type { Clock } from "../clock";
import { READ_LANGUAGES } from "../data/readLanguages";
import { languageName } from "../data/languages";
import { dirOf, useI18n, type StringKey } from "../i18n";
import { shown } from "../i18n/pending";
import type { ReplayPlayer } from "../media/replay";
import type { RecordData, RecordSearchResult } from "../protocol/records";
import {
  ADMIN_FILTERS,
  ago,
  clockTime,
  countsOf,
  dayTime,
  dayTimeRange,
  duration,
  filterWord,
  roomLabel,
  stateChip,
  visibleRooms,
  type AdminFilter,
  type AdminRoom,
} from "./adminList";
import { ConfirmSheet } from "./ConfirmSheet";
import {
  BackIcon,
  ChevronEndIcon,
  EyeIcon,
  EyeOffIcon,
  ListIcon,
  PhrasesIcon,
  RefreshIcon,
  SearchIcon,
  SeatsIcon,
  TrashIcon,
} from "./icons";
import { LockIcon } from "./Knocks";
import { LanguagePicker } from "./LanguagePicker";
import { RecordScreen, type RecordStatus } from "./RecordScreen";
import { withName } from "./RoomsList";

export type { AdminRoom } from "./adminList";
export const ADMIN_ROOMS_PATH = "/api/admin/rooms";
export const adminRecordPath = (roomId: string): string => `${ADMIN_ROOMS_PATH}/${encodeURIComponent(roomId)}/record`;
export const adminAudioPath = (segmentId: string): string => `/api/admin/audio/${encodeURIComponent(segmentId)}`;

/** How often the header's "updated ..." and the rows' relative times are redrawn (ASSUMPTION: a glance-level freshness). */
export const ADMIN_TICK_MS = 30_000;

type Fetch = typeof fetch;

async function getJson<T>(fetchImpl: Fetch, url: string, password: string): Promise<T | null> {
  try {
    const res = await fetchImpl(url, { headers: { Authorization: `Bearer ${password}` }, cache: "no-store" });
    if (res.status !== 200) return null;
    return (await res.json()) as T;
  } catch {
    return null;
  }
}

async function deleteRoom(fetchImpl: Fetch, roomId: string, password: string): Promise<"deleted" | "open" | "failed"> {
  try {
    const res = await fetchImpl(adminRecordPath(roomId), {
      method: "DELETE",
      headers: { Authorization: `Bearer ${password}` },
      cache: "no-store",
    });
    if (res.status === 200) return "deleted";
    if (res.status === 409) return "open";
    return "failed";
  } catch {
    return "failed";
  }
}

/** The words of a pending-aware key: its own once wired, its stand-in's while pending, null for nothing. */
type MaybeT = (key: StringKey, vars?: Record<string, string | number>) => string | null;

/** A listing's words: listed open / listed with key / unlisted (the Majlis sheet's own options). */
export const listingWord = (listing: string): StringKey =>
  listing === "open" ? "listedOpen" : listing === "key" ? "listedKey" : "unlisted";

const at = (iso: string | null): number | null => (iso ? Date.parse(iso) : null);

function Chip({ room, className = "" }: { room: AdminRoom; className?: string }) {
  const { t } = useI18n();
  const c = stateChip(room.status);
  return (
    <span className={`admin-state admin-state-${c.kind} ${className}`.trim()} data-spec="UI-C37">
      {c.dot && <span className="live-dot" aria-hidden="true" />}
      {t(c.word)}
    </span>
  );
}

export function AdminScreen({
  fetchImpl,
  replay,
  clock,
  reduced,
  readLang,
  onReadLang,
  wallNow = Date.now,
  host = "",
  dayShort = false,
  chipCompact = false,
}: {
  /** the record header's narrow forms, as the host's record (MAJLIS-153 ruling 2; MAJLIS-167 ruling B) */
  dayShort?: boolean;
  chipCompact?: boolean;
  fetchImpl: Fetch;
  replay: ReplayPlayer;
  clock: Clock;
  reduced: boolean;
  readLang: string;
  onReadLang: (tag: string) => void;
  /** MAJLIS-176: the app's injected wall clock (relative times, "updated ..."), never read here directly */
  wallNow?: () => number;
  /** MAJLIS-176: the server's host name, the header's sub-line (frame b) */
  host?: string;
}) {
  const { t, lang } = useI18n();
  const tp: MaybeT = (key, vars) => {
    const k = shown(key);
    return k === null ? null : t(k, vars);
  };
  const [typed, setTyped] = useState("");
  const [reveal, setReveal] = useState(false);
  const [password, setPassword] = useState<string | null>(null);
  const [rooms, setRooms] = useState<AdminRoom[] | null>(null);
  const [loadedAt, setLoadedAt] = useState<number | null>(null);
  const [refused, setRefused] = useState(false);
  const [filter, setFilter] = useState<AdminFilter>("all");
  const [search, setSearch] = useState("");
  const [open, setOpen] = useState<string | null>(null);
  const [record, setRecord] = useState<RecordData | null>(null);
  const [status, setStatus] = useState<RecordStatus>("loading");
  const [tick, setTick] = useState(0);
  const [, setClockTick] = useState(0);
  const [confirming, setConfirming] = useState(false);
  const [picking, setPicking] = useState(false);
  const [deleteNote, setDeleteNote] = useState<string | null>(null);
  const [deleting, setDeleting] = useState(false);
  const [deleteFailed, setDeleteFailed] = useState<string | null>(null);
  const [deleteRefused, setDeleteRefused] = useState<string | null>(null);

  const loadRooms = useCallback(
    async (pw: string) => {
      const res = await getJson<{ rooms: AdminRoom[] }>(fetchImpl, ADMIN_ROOMS_PATH, pw);
      if (res === null) {
        setRefused(true);
        setPassword(null);
        setRooms(null);
        return;
      }
      setRefused(false);
      setPassword(pw);
      setRooms(res.rooms);
      setLoadedAt(wallNow());
    },
    [fetchImpl, wallNow],
  );

  // the open room's record, in the reading language; re-read while translations are still owed
  useEffect(() => {
    if (open === null || password === null) return;
    let alive = true;
    void getJson<RecordData>(fetchImpl, `${adminRecordPath(open)}?lang=${encodeURIComponent(readLang)}`, password).then(
      (res) => {
        if (!alive) return;
        if (res === null) {
          setStatus("failed");
          return;
        }
        setRecord(res);
        setStatus("ready");
      },
    );
    return () => {
      alive = false;
    };
  }, [open, password, readLang, fetchImpl, tick]);

  const waiting = record?.phrases.some((p) => p.pending) ?? false;
  useEffect(() => {
    if (!waiting || open === null) return;
    const id = clock.setTimeout(() => setTick((n) => n + 1), 2000);
    return () => clock.clearTimeout(id);
  }, [waiting, open, clock, tick]);

  // the list's relative times ("updated 3 min ago", "59 min ago") are redrawn on the injected clock
  const signedIn = password !== null;
  useEffect(() => {
    if (!signedIn) return;
    const id = clock.setInterval(() => setClockTick((n) => n + 1), ADMIN_TICK_MS);
    return () => clock.clearInterval(id);
  }, [signedIn, clock]);

  const openRoom = (roomId: string) => {
    if (password === null) return;
    replay.reset();
    replay.setAuthorization(`Bearer ${password}`);
    replay.setAudioPath(adminAudioPath);
    setRecord(null);
    setStatus("loading");
    setConfirming(false);
    setPicking(false);
    setDeleteFailed(null);
    setDeleteRefused(null);
    setDeleteNote(null);
    setOpen(roomId);
  };

  const closeRoom = () => {
    replay.reset();
    replay.setAuthorization(null);
    replay.setAudioPath(null);
    setOpen(null);
    setRecord(null);
    setConfirming(false);
    setDeleteFailed(null);
    setDeleteRefused(null);
    if (password !== null) void loadRooms(password);
  };

  /** Lock (frame b): the password is dropped from memory and the sign-in card comes back. */
  const lock = () => {
    setPassword(null);
    setRooms(null);
    setLoadedAt(null);
    setRefused(false);
    setFilter("all");
    setSearch("");
    setDeleteNote(null);
    setReveal(false);
    setTyped("");
  };

  useEffect(
    () => () => {
      replay.setAuthorization(null);
      replay.setAudioPath(null);
    },
    [replay],
  );

  const noSearch = useCallback(async (): Promise<RecordSearchResult | null> => null, []);

  const removeOpen = async () => {
    if (open === null || password === null) return;
    setDeleting(true);
    setDeleteFailed(null);
    setDeleteRefused(null);
    const done = await deleteRoom(fetchImpl, open, password);
    setDeleting(false);
    if (done === "deleted") {
      setConfirming(false);
      setDeleteNote(t("adminDeleteDone"));
      closeRoom();
      return;
    }
    // the sheet stays: a room still open is the server's reason, under the red row in --warn (frame d); anything else
    // is the existing "could not" line
    if (done === "open") setDeleteRefused(t("adminDeleteOpen"));
    else setDeleteFailed(t("adminDeleteFailed"));
  };

  const now = wallNow();

  // ------------------------------------------------------------------------------------------------ one room (c), (d)
  if (open !== null) {
    const room = rooms?.find((r) => r.room_id === open) ?? null;
    const label = room ? roomLabel(room) : "";
    const created = room ? at(room.created) : null;
    const last = room ? at(room.last_activity) : null;
    const askKey = shown("adminDeleteNamed") ?? "adminDeleteConfirm";
    const question = askKey === "adminDeleteNamed" ? withName(t, askKey, label) : t(askKey);
    const inAll = room ? tp("adminInAll", { n: room.seats_total }) : null;
    const people = room
      ? inAll !== null
        ? (tp("adminInNow", { n: room.seated_now, m: inAll }) ?? String(room.seats_total))
        : String(room.seats_total)
      : "";
    return (
      <div className="admin-room" data-spec="UI-C37">
        <header className="admin-bar admin-room-bar" data-spec="UI-C37">
          <button type="button" className="icon-button admin-back" aria-label={t("back")} onClick={closeRoom}>
            <BackIcon />
          </button>
          <div className="admin-bar-title">
            <span className="admin-bar-name" dir="auto">
              {label}
            </span>
            {room && (
              <span className="admin-bar-sub">
                {/* the consultant's ruling 2 (2026-10-06): state · host · the day-time, no "Started" (the facts carry the times) */}
                {t(stateChip(room.status).word)} · <bdi>{room.host_name}</bdi>
                {created !== null && <> · {dayTime(lang, created)}</>}
              </span>
            )}
          </div>
          {room?.status !== "deleted" && (
            <button type="button" className="text-link admin-delete" onClick={() => setConfirming(true)}>
              <TrashIcon />
              {t("adminDelete")}
            </button>
          )}
        </header>
        {room && (
          <dl className="admin-facts" data-spec="UI-C37">
            <div className="admin-fact">
              <dt>{t("adminSeats")}</dt>
              <dd>{people}</dd>
            </div>
            <div className="admin-fact">
              <dt>{t("adminPhrases")}</dt>
              <dd>{room.phrases}</dd>
            </div>
            <div className="admin-fact">
              <dt>{t("adminLast")}</dt>
              <dd>{last !== null ? `${clockTime(lang, last)} · ${ago(lang, last, now)}` : "–"}</dd>
            </div>
            <div className="admin-fact">
              <dt>{t("listedMark")}</dt>
              <dd>
                {t(listingWord(room.listing))}
                {room.locked && <> · {t("locked")}</>}
              </dd>
            </div>
            {/* the consultant's ruling 1 (2026-10-06): the record's own header is not drawn here, so the reading language
                it carried is this fifth fact - its name a text link that opens the same picker */}
            <div className="admin-fact admin-fact-read">
              <dt>{t("read")}</dt>
              <dd>
                <button
                  type="button"
                  className="text-link admin-read"
                  aria-label={t("readingChip", { lang: languageName(readLang, lang) })}
                  onClick={() => setPicking(true)}
                  data-spec="UI-C9 D-06 D-21"
                >
                  <span lang={readLang} dir={dirOf(readLang)}>
                    {READ_LANGUAGES.find((l) => l.tag === readLang)?.native ?? languageName(readLang, lang)}
                  </span>
                </button>
              </dd>
            </div>
          </dl>
        )}
        {confirming && (
          <ConfirmSheet
            question={question}
            body={tp("adminDeleteBody") ?? undefined}
            action={t("adminDeleteYes")}
            icon={<TrashIcon />}
            failed={deleteFailed ?? undefined}
            refused={deleteRefused ?? undefined}
            busy={deleting}
            spec="UI-C37 UI-C14 D-56"
            onConfirm={() => void removeOpen()}
            onCancel={() => {
              setConfirming(false);
              setDeleteFailed(null);
              setDeleteRefused(null);
            }}
          />
        )}
        {picking && (
          <LanguagePicker
            title={t("read")}
            list={READ_LANGUAGES}
            value={readLang}
            spec="UI-C9 UI-C2 D-06 D-21"
            onPick={(tag) => {
              onReadLang(tag);
              setPicking(false);
            }}
            onClose={() => setPicking(false)}
          />
        )}
        <RecordScreen
          header={false}
          record={record}
          status={status}
          waiting={waiting}
          readLang={readLang}
          onReadLang={onReadLang}
          replay={replay}
          reduced={reduced}
          clock={clock}
          onBack={closeRoom}
          onRetry={() => {
            setStatus("loading");
            setTick((n) => n + 1);
          }}
          onSearch={noSearch}
          dayShort={dayShort}
          chipCompact={chipCompact}
        />
      </div>
    );
  }

  // ------------------------------------------------------------------------------------------------ sign in (a), (f)
  if (password === null) {
    const line = tp("adminSignIn");
    const foot = tp("adminFootnote");
    const eye = tp(reveal ? "adminHidePassword" : "adminShowPassword");
    return (
      <main className="admin admin-signin" data-spec="UI-C37">
        <form
          className="admin-card"
          onSubmit={(e) => {
            e.preventDefault();
            if (typed) void loadRooms(typed);
            setTyped("");
          }}
        >
          <span className="admin-disc" aria-hidden="true">
            <LockIcon />
          </span>
          <h1 className="admin-card-title">{t("adminTitle")}</h1>
          {line && <p className="admin-card-line">{line}</p>}
          <label className="admin-label" htmlFor="admin-password">
            {t("adminPassword")}
          </label>
          <div className={refused ? "admin-field refused" : "admin-field"}>
            <input
              id="admin-password"
              type={reveal ? "text" : "password"}
              autoComplete="off"
              autoCapitalize="off"
              autoCorrect="off"
              spellCheck={false}
              value={typed}
              aria-invalid={refused || undefined}
              aria-describedby={refused ? "admin-refused" : undefined}
              onChange={(e) => {
                setTyped(e.target.value);
                setRefused(false);
              }}
              data-testid="admin-password"
            />
            {eye && (
              <button
                type="button"
                className="icon-button admin-eye"
                aria-label={eye}
                aria-pressed={reveal}
                onClick={() => setReveal((v) => !v)}
              >
                {reveal ? <EyeOffIcon /> : <EyeIcon />}
              </button>
            )}
          </div>
          {refused && (
            <p id="admin-refused" className="form-error admin-refused" role="alert">
              {t("adminUnavailable")}
            </p>
          )}
          <button type="submit" className="primary admin-submit">
            {t("adminEnter")}
          </button>
        </form>
        {foot && <p className="admin-footnote">{foot}</p>}
      </main>
    );
  }

  // ------------------------------------------------------------------------------------------------ all rooms (b), (e), (h)
  const all = rooms ?? [];
  const counts = countsOf(all);
  const shownRooms = visibleRooms(all, filter, search);
  const updated =
    loadedAt === null
      ? null
      : now - loadedAt < 45_000
        ? tp("adminUpdatedNow")
        : tp("adminUpdated", { time: ago(lang, loadedAt, now) });
  const lockWord = tp("adminLock");
  const tiles = [tp("adminOpenNow"), tp("adminPeopleIn"), tp("adminRooms")];
  const searchWord = tp("adminSearch");
  const allWord = tp("adminAll");
  const newest = tp("adminNewest");
  const count = tp("adminCount", { n: shownRooms.length, total: all.length });
  const removed = tp("adminRemoved");
  const stateHead = tp("adminState");
  const emptyBody = tp("adminEmptyBody");

  const rowCells = (r: AdminRoom): { card2: ReactNode; sub: string; people: string; lastCard: string; lastTable: string; aria: string } => {
    const c = stateChip(r.status);
    const created = Date.parse(r.created);
    const closed = at(r.closed);
    const last = at(r.last_activity);
    const when =
      r.status === "open"
        ? t("started", { t: dayTime(lang, created) })
        : r.status === "closed" && closed !== null
          ? dayTimeRange(lang, created, closed)
          : dayTime(lang, created);
    const marks = [
      ...(r.listing !== "unlisted" ? [t("listedMark")] : []),
      ...(r.locked ? [t("locked")] : []),
      ...(r.status === "deleted" && removed ? [removed] : []),
    ];
    const people =
      r.status === "open" ? (tp("adminInNow", { n: r.seated_now, m: r.seats_total }) ?? String(r.seats_total)) : String(r.seats_total);
    const lastCard =
      r.status === "open"
        ? last !== null
          ? ago(lang, last, now)
          : ""
        : r.status === "closed" && closed !== null
          ? duration(lang, closed - created)
          : (removed ?? "");
    const lastTable =
      r.status === "deleted" || last === null
        ? "–"
        : r.status === "open"
          ? `${clockTime(lang, last)} · ${ago(lang, last, now)}`
          : dayTime(lang, last);
    const aria = [
      roomLabel(r),
      t(c.word),
      r.host_name,
      when,
      ...marks,
      `${t("adminSeats")} ${people}`,
      `${t("adminPhrases")} ${r.phrases}`,
      ...(r.status === "deleted" ? [] : [`${t("adminLast")} ${lastTable}`]),
    ].join(", ");
    return {
      card2: (
        <>
          <bdi>{r.host_name}</bdi> · {when}
        </>
      ),
      sub: [when, ...marks].join(" · "),
      people,
      lastCard,
      lastTable,
      aria,
    };
  };

  return (
    <main className="admin admin-list" data-spec="UI-C37">
      <header className="admin-bar" data-spec="UI-C37">
        <div className="admin-bar-title">
          <h1 className="admin-bar-name">{t("adminTitle")}</h1>
          <span className="admin-bar-sub">
            {host}
            {updated && (host ? ` · ${updated}` : updated)}
          </span>
        </div>
        <button
          type="button"
          className="round-button admin-refresh"
          aria-label={t("adminRefresh")}
          onClick={() => void loadRooms(password)}
        >
          <RefreshIcon />
        </button>
        {lockWord && (
          <button type="button" className="text-link muted admin-lock" onClick={lock}>
            {lockWord}
          </button>
        )}
      </header>
      {deleteNote && (
        <p className="admin-status-line" role="status">
          {deleteNote}
        </p>
      )}
      {rooms !== null && rooms.length === 0 && (
        <div className="admin-empty" data-spec="UI-C37">
          <span className="admin-disc muted" aria-hidden="true">
            <PhrasesIcon />
          </span>
          <h2 className="admin-empty-title">{t("adminEmpty")}</h2>
          {emptyBody && <p className="admin-empty-body">{emptyBody}</p>}
        </div>
      )}
      {rooms !== null && rooms.length > 0 && (
        <div className="admin-body">
          {(tiles.every((w) => w !== null) || searchWord) && (
            <div className="admin-top">
              {tiles.every((w) => w !== null) && (
                <div className="admin-tiles" data-spec="UI-C37">
                  <div className="admin-tile">
                    <b>
                      <span className="live-dot" aria-hidden="true" />
                      {counts.openNow}
                    </b>
                    <span>{tiles[0]}</span>
                  </div>
                  <div className="admin-tile">
                    <b>{counts.peopleIn}</b>
                    <span>{tiles[1]}</span>
                  </div>
                  <div className="admin-tile">
                    <b>{counts.rooms}</b>
                    <span>{tiles[2]}</span>
                  </div>
                </div>
              )}
              {searchWord && (
                <div className="admin-search">
                  <SearchIcon />
                  <input
                    type="search"
                    className="admin-search-input"
                    dir="auto"
                    autoComplete="off"
                    placeholder={searchWord}
                    aria-label={searchWord}
                    value={search}
                    onChange={(e) => setSearch(e.target.value)}
                    data-testid="admin-search"
                  />
                </div>
              )}
            </div>
          )}
          {allWord && (
            <div className="admin-chips" role="group" aria-label={stateHead ?? undefined} data-spec="UI-C37">
              {ADMIN_FILTERS.map((f) => (
                <button
                  key={f}
                  type="button"
                  className={f === filter ? "admin-chip on" : "admin-chip"}
                  aria-pressed={f === filter}
                  data-filter={f}
                  onClick={() => setFilter(f)}
                >
                  <span className="admin-chip-face">
                    {f === "all" ? allWord : t(filterWord(f))}
                    <span className="admin-chip-n">{counts.byFilter[f]}</span>
                  </span>
                </button>
              ))}
            </div>
          )}
          <div className="admin-rooms" data-spec="UI-C37">
            <div className="admin-thead" aria-hidden="true">
              <span>{t("roomName")}</span>
              <span>{stateHead ?? ""}</span>
              <span>{t("adminHost")}</span>
              <span className="num">{t("adminSeated")}</span>
              <span className="num">{t("adminSeats")}</span>
              <span className="num">{t("adminPhrases")}</span>
              <span>{t("adminLast")}</span>
              <span />
            </div>
            <ul className="admin-list-rows">
              {shownRooms.map((r) => {
                const c = stateChip(r.status);
                const cells = rowCells(r);
                const body = (
                  <>
                    <span className="admin-c-room">
                      <span className="admin-l1">
                        <span className="admin-name" dir="auto">
                          {roomLabel(r)}
                        </span>
                        <Chip room={r} className="admin-card-only" />
                        {(r.listing !== "unlisted" || r.locked) && (
                          <span className="admin-tags admin-card-only" aria-hidden="true">
                            {r.listing !== "unlisted" && <ListIcon />}
                            {r.locked && <LockIcon />}
                          </span>
                        )}
                      </span>
                      <span className="admin-l2 admin-card-only">{cells.card2}</span>
                      <span className="admin-sub admin-table-only">{cells.sub}</span>
                      <span className="admin-l3 admin-card-only">
                        <span>
                          <SeatsIcon />
                          {cells.people}
                        </span>
                        {r.status !== "deleted" && (
                          <span>
                            <PhrasesIcon />
                            {r.phrases}
                          </span>
                        )}
                        {cells.lastCard && <span>{cells.lastCard}</span>}
                      </span>
                    </span>
                    <span className="admin-c-state admin-table-only">
                      <Chip room={r} />
                    </span>
                    <span className="admin-c-host admin-table-only">
                      <bdi>{r.host_name}</bdi>
                    </span>
                    <span className="admin-c-num admin-table-only">{r.seated_now}</span>
                    <span className="admin-c-num admin-table-only">{r.seats_total}</span>
                    <span className="admin-c-num admin-table-only">{r.phrases}</span>
                    <span className="admin-c-last admin-table-only">{cells.lastTable}</span>
                    {c.opens ? <ChevronEndIcon /> : <span className="admin-c-chev" />}
                  </>
                );
                return (
                  <li key={r.room_id}>
                    {c.opens ? (
                      <button
                        type="button"
                        className="admin-row"
                        data-room={r.room_id}
                        aria-label={cells.aria}
                        onClick={() => openRoom(r.room_id)}
                      >
                        {body}
                      </button>
                    ) : (
                      <div className="admin-row dim" data-room={r.room_id} aria-label={cells.aria} role="group">
                        {body}
                      </div>
                    )}
                  </li>
                );
              })}
            </ul>
          </div>
          {newest && count && (
            <p className="admin-foot">
              {newest} · {count}
            </p>
          )}
        </div>
      )}
    </main>
  );
}
